import { useSelector } from 'react-redux';
import { Box, Link, Typography } from '@mui/material';

import MicrositeImages from '~/components/atoms/microsite-images/MicrositeImages';
import ListOfLinksPreviewSocials from '~/components/organisms/previewBar/components/list-of-links/components/ListOfLinksPreviewSocials';
import { getListOfLinksReducer } from '~/store/reducers/previewScreens/list-of-links/selectors';
import { COLORS } from '~/styles/colors';
import { mainPreviewContainerStyles } from '~/styles/common';
import { getScalValue } from '~/utils/common';

type LIST_OF_LINKS_TEMPLATE_2Props = {
  isMyMicrosite?: boolean;
};
export function LIST_OF_LINKS_TEMPLATE_2({ isMyMicrosite }: LIST_OF_LINKS_TEMPLATE_2Props) {
  const listOfLinksState = useSelector(getListOfLinksReducer);

  const {
    micrositeStyles,
    bacgkroundColor,
    fonts: { title, text },
    content: { linkList, images, imagesType },
    fontsSize: { titleSize, descriptionSize },
    basicInformation: {
      logoImage: { scale, borderRadius, rotate, url },
      coverImage,
      h1,
      h2,
    },
    micrositeData,
  } = listOfLinksState;

  const { socials = [] } = micrositeData ?? {};

  const showTopBlock = !!url || h1 || h2;
  const isCoverImageWithoutLogo = coverImage?.url && !url ? 14 : -1;

  return (
    <Box
      sx={[
        {
          ...mainPreviewContainerStyles,
          alignItems: 'normal',
          background: bacgkroundColor[0],
          ...micrositeStyles?.container,
        },
        isMyMicrosite && { margin: 0, minHeight: '100vh', borderRadius: 0 },
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          minWidth: '100%',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            backgroundImage: `url(${coverImage.url})`,
            width: '100%',
            height: 110,
            position: 'absolute',
            backgroundSize: '100% auto',
          }}
        />
        <Box sx={{ padding: '0 1rem' }}>
          {showTopBlock && (
            <Box sx={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>
              {!!url && (
                <Box
                  sx={{
                    backgroundColor: COLORS.white,
                    borderRadius: `${borderRadius}px`,
                    backgroundSize: 'cover',
                    transform: `scale(${getScalValue(scale)})`,
                    width: 75,
                    height: 75,
                    padding: 0.5,
                    mt: 6.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    alt="list of links image"
                    src={url}
                    style={{
                      width: 70,
                      height: 70,
                      borderRadius: `${borderRadius}px`,
                      objectFit: 'cover',
                      transform: `rotate(${rotate}deg)`,
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          <Typography
            sx={{ fontFamily: title, fontSize: titleSize, mb: 1, mt: isCoverImageWithoutLogo, wordBreak: 'break-word' }}
          >
            {h1}
          </Typography>
          <Typography sx={{ fontFamily: text, fontSize: descriptionSize, mb: 2, wordBreak: 'break-word' }}>
            {h2}
          </Typography>

          {!!socials.length && (
            <ListOfLinksPreviewSocials
              socialLinks={socials}
              sx={{
                mb: 2,
                mt: 2,
              }}
            />
          )}

          {linkList.map((item, index) => {
            return (
              <Box
                sx={{
                  backgroundColor: bacgkroundColor[1],
                  mb: 1,
                  padding: '8px 10px',
                  borderRadius: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  minHeight: 44,
                  fontFamily: text,
                  boxShadow: '0 0 13px 0 rgba(0, 0, 0, 0.05)',
                }}
                onClick={() => window.open(item?.url, '_blank')}
                key={index}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%',
                  }}
                >
                  {item?.previewLink && (
                    <img
                      alt="cover"
                      src={item?.previewLink}
                      style={{
                        borderRadius: '8px',
                        height: 28,
                        width: 28,
                        objectFit: 'cover',
                      }}
                    />
                  )}
                  <Link
                    target="_blank"
                    href={item?.url}
                    sx={{ color: bacgkroundColor[2], fontSize: 10, textDecoration: 'none', lineBreak: 'anywhere' }}
                  >
                    {item?.linkText}
                  </Link>
                </Box>
              </Box>
            );
          })}
          <MicrositeImages data={images} type={imagesType} />
        </Box>
      </Box>
    </Box>
  );
}
